import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ["nav", "control", "link", "container", "item"];

  connect() {
    this.offsetAmount = 2;
    this.activeIndex = 0;
    this.controlsWidth = this.controlTargets[0].offsetWidth;
    this.overflowDir = null;

    this.setHeight();
    this.setOverflow();

    this.linkTargets.forEach((link, index) => {
      this.handleClick(link, index);
    });

    this.controlTargets.forEach((control) => {
      control.addEventListener("click", (e) => {
        e.preventDefault();
        if (control.classList.contains("tabs__control--right")) {
          this.mouseNav("right");
        } else {
          this.mouseNav("left");
        }
      });
    });

    this.navTarget.addEventListener("scroll", () => this.setOverflow());

    window.addEventListener(
      "resize",
      this.debounce(() => {
        this.setHeight();
        this.setOverflow();
      })
    );

    this.linkTargets.forEach((tab, i) => {
      tab.addEventListener("keydown", (e) => this.handleKeydown(e, i));
      tab.addEventListener("focus", () => this.ensureVisible(tab));
    });
  }

  getFirstVisible() {
    const tabsLeft = this.element.getBoundingClientRect().left;
    const visibleItems = this.itemTargets.filter(
      (item) =>
        item.getBoundingClientRect().right >
        tabsLeft + this.controlsWidth + this.offsetAmount
    );

    return visibleItems[1].offsetLeft - this.controlsWidth;
  }

  getLastHidden() {
    const tabsLeft = this.element.getBoundingClientRect().left;
    const hiddenItems = this.itemTargets.filter(
      (item) => item.getBoundingClientRect().left < tabsLeft
    );

    return hiddenItems[hiddenItems.length - 1].offsetLeft - this.controlsWidth;
  }

  setOverflow() {
    const tabsLeft = this.element.getBoundingClientRect().left;
    const tabsRight = this.element.getBoundingClientRect().right;
    const firstTabLeft = this.itemTargets[0].getBoundingClientRect().left;
    const lastTabRight = Math.floor(
      this.itemTargets[this.itemTargets.length - 1].getBoundingClientRect()
        .right
    );

    if (firstTabLeft < tabsLeft && lastTabRight > tabsRight) {
      this.updateOverflow("both");
    } else if (firstTabLeft < tabsLeft) {
      this.updateOverflow("left");
    } else if (lastTabRight > tabsRight) {
      this.updateOverflow("right");
    } else {
      this.updateOverflow("none");
    }
  }

  updateOverflow(direction) {
    if (this.overflowDir !== direction) {
      this.overflowDir = direction;
      this.element.setAttribute("data-overflow", direction);
    }
  }

  mouseNav(direction) {
    if (direction === "right") {
      this.navTarget.scrollLeft = this.getFirstVisible();
    } else {
      this.navTarget.scrollLeft = this.getLastHidden();
    }
  }

  setHeight() {
    const navHeight = this.navTarget.offsetHeight;
    const containerHeight = this.containerTargets[this.activeIndex].offsetHeight;
    const totalHeight = navHeight + containerHeight;
    this.element.style.height = `${totalHeight}px`;
  }

  goToTab(index) {
    if (index !== this.activeIndex && index >= 0 && index < this.linkTargets.length) {
      this.linkTargets[this.activeIndex].removeAttribute("aria-selected");
      this.linkTargets[this.activeIndex].setAttribute("tabindex", "-1");
      this.linkTargets[index].setAttribute("aria-selected", "true");
      this.linkTargets[index].removeAttribute("tabindex");
      this.linkTargets[index].focus();
      this.containerTargets[this.activeIndex].classList.remove("is-active");
      this.containerTargets[index].classList.add("is-active");
      this.activeIndex = index;
      this.setHeight();
    }
  }

  ensureVisible(item) {
    const tabsLeft = this.element.getBoundingClientRect().left;
    const tabsRight = this.element.getBoundingClientRect().right;

    if (
      Math.floor(item.getBoundingClientRect().right) >
      tabsRight - this.controlsWidth
    ) {
      if (
        this.element.getAttribute("data-overflow") === "right" ||
        this.element.getAttribute("data-overflow") === "both"
      ) {
        this.navTarget.scrollLeft = item.offsetLeft - this.controlsWidth;
      }
    } else if (
      Math.floor(item.getBoundingClientRect().left) <
      tabsLeft + this.controlsWidth
    ) {
      if (
        this.element.getAttribute("data-overflow") === "left" ||
        this.element.getAttribute("data-overflow") === "both"
      ) {
        this.navTarget.scrollLeft = item.offsetLeft - this.controlsWidth;
      }
    }
  }

  handleClick(link, index) {
    link.addEventListener("click", (e) => {
      e.preventDefault();
      this.goToTab(index);
    });
  }

  debounce(func) {
    let timer;
    return function debounceFunction(event) {
      if (timer) clearTimeout(timer);
      timer = setTimeout(func, 200, event);
    };
  }

  handleKeydown(e, i) {
    let dir;
    if (e.which === 37) {
      dir = i - 1;
    } else if (e.which === 39) {
      dir = i + 1;
    } else if (e.which === 40) {
      dir = "down";
    } else {
      dir = null;
    }

    if (dir !== null) {
      e.preventDefault();
      if (dir === "down") {
        const activePanel = this.containerTargets[this.activeIndex];
        activePanel.focus();
      } else if (this.linkTargets[dir]) {
        this.goToTab(dir);
      }
    }
  }
}
